import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Select,
  Table,
  Tag,
  Typography,
  Spin,
  Alert,
  Tabs,
  Progress,
  Space,
} from 'antd';
import {
  UserOutlined,
  AppstoreOutlined,
  FileTextOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  TrophyOutlined,
  ShoppingOutlined,
} from '@ant-design/icons';
import {
  analyticsService,
  AnalyticsDashboard,
  UserAnalytics,
  PostAnalytics,
  MostReportedUser,
  MostViewedProduct,
  CategoryAnalytics,
} from '../services/analyticsService';
import { getImageUrl } from '../config/api';

const { Title } = Typography;
const { Option } = Select;

const Analytics: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<string>('month');
  const [activeTab, setActiveTab] = useState<string>('overview');

  // Dashboard data
  const [dashboardData, setDashboardData] = useState<AnalyticsDashboard | null>(null);
  const [userAnalytics, setUserAnalytics] = useState<UserAnalytics | null>(null);
  const [postAnalytics, setPostAnalytics] = useState<PostAnalytics | null>(null);
  const [mostReportedUsers, setMostReportedUsers] = useState<MostReportedUser[]>([]);
  const [mostViewedProducts, setMostViewedProducts] = useState<{
    mostViewedPets: MostViewedProduct[];
    mostContactedPets: MostViewedProduct[];
  } | null>(null);
  const [categoryAnalytics, setCategoryAnalytics] = useState<CategoryAnalytics | null>(null);

  useEffect(() => {
    fetchAllData();
  }, [period]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [dashboard, users, posts, reported, products, categories] = await Promise.all([
        analyticsService.getDashboard(period),
        analyticsService.getUserAnalytics(period, 'day'),
        analyticsService.getPostAnalytics(period),
        analyticsService.getMostReportedUsers(10),
        analyticsService.getMostViewedProducts(10, period),
        analyticsService.getCategoryAnalytics(),
      ]);

      setDashboardData(dashboard.data);
      setUserAnalytics(users.data);
      setPostAnalytics(posts.data);
      setMostReportedUsers(reported.data);
      setMostViewedProducts(products.data);
      setCategoryAnalytics(categories.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !dashboardData) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return <Alert message="Error" description={error} type="error" showIcon />;
  }

  const formatGrowth = (growth: number) => {
    return `${growth >= 0 ? '+' : ''}${growth.toFixed(1)}%`;
  };

  const mostReportedColumns = [
    {
      title: 'User',
      key: 'user',
      render: (record: MostReportedUser) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <img
            src={record.user.profileImage || 'https://via.placeholder.com/40'}
            alt={record.user.username}
            style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }}
            onError={(e) => {
              (e.target as HTMLImageElement).src = 'https://via.placeholder.com/40';
            }}
          />
          <div>
            <div>{record.user.name || record.user.username}</div>
            <div style={{ fontSize: '12px', color: '#999' }}>@{record.user.username}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Total Reports',
      dataIndex: 'reportCount',
      key: 'reportCount',
      sorter: (a: MostReportedUser, b: MostReportedUser) => a.reportCount - b.reportCount,
    },
    {
      title: 'Pending',
      dataIndex: 'pendingCount',
      key: 'pendingCount',
      render: (count: number) => <Tag color="orange">{count}</Tag>,
    },
    {
      title: 'Resolved',
      dataIndex: 'resolvedCount',
      key: 'resolvedCount',
      render: (count: number) => <Tag color="green">{count}</Tag>,
    },
    {
      title: 'Status',
      key: 'status',
      render: (record: MostReportedUser) => (
        <Space>
          {record.user.isBanned && <Tag color="red">Banned</Tag>}
          {record.user.isSuspended && <Tag color="orange">Suspended</Tag>}
          {record.user.isVerified && <Tag color="blue">Verified</Tag>}
        </Space>
      ),
    },
  ];

  const mostViewedColumns = [
    {
      title: 'Product',
      key: 'product',
      render: (record: MostViewedProduct) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img
            src={getImageUrl(record.images?.[0]?.url || '')}
            alt={record.title}
            style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }}
            onError={(e) => {
              (e.target as HTMLImageElement).src = 'https://via.placeholder.com/50';
            }}
          />
          <div>
            <div style={{ fontWeight: 500 }}>{record.title}</div>
            <div style={{ fontSize: '12px', color: '#999' }}>
              {record.petCategory?.name} {record.breed && `• ${record.breed.name}`}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Owner',
      key: 'owner',
      render: (record: MostViewedProduct) => record.owner?.name || record.owner?.username || 'Unknown',
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (price: number) => `₹${price?.toLocaleString() || 0}`,
    },
    {
      title: 'Views',
      dataIndex: 'viewCount',
      key: 'viewCount',
      sorter: (a: MostViewedProduct, b: MostViewedProduct) => a.viewCount - b.viewCount,
      render: (count: number) => <Tag color="blue">{count}</Tag>,
    },
    {
      title: 'Contacts',
      dataIndex: 'contactCount',
      key: 'contactCount',
      sorter: (a: MostViewedProduct, b: MostViewedProduct) => a.contactCount - b.contactCount,
      render: (count: number) => <Tag color="green">{count}</Tag>,
    },
  ];

  const tabItems = [
    {
      key: 'overview',
      label: '📊 Overview',
      children: dashboardData && (
        <div>
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Users"
                  value={dashboardData.summary.totalUsers}
                  prefix={<UserOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
                <div style={{ marginTop: 8 }}>
                  <span style={{ color: dashboardData.growth.users.trend === 'up' ? '#52c41a' : '#ff4d4f' }}>
                    {dashboardData.growth.users.trend === 'up' ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                    {formatGrowth(dashboardData.growth.users.growth)}
                  </span>
                  <span style={{ marginLeft: 8, color: '#8c8c8c', fontSize: '12px' }}>
                    ({dashboardData.growth.users.current} this {period})
                  </span>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Active Users"
                  value={dashboardData.summary.activeUsers}
                  prefix={<UserOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
                <div style={{ marginTop: 8, color: '#8c8c8c', fontSize: '12px' }}>
                  Last 30 days
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Posts"
                  value={dashboardData.summary.totalPosts}
                  prefix={<FileTextOutlined />}
                  valueStyle={{ color: '#722ed1' }}
                />
                <div style={{ marginTop: 8 }}>
                  <span style={{ color: dashboardData.growth.posts.trend === 'up' ? '#52c41a' : '#ff4d4f' }}>
                    {dashboardData.growth.posts.trend === 'up' ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                    {formatGrowth(dashboardData.growth.posts.growth)}
                  </span>
                  <span style={{ marginLeft: 8, color: '#8c8c8c', fontSize: '12px' }}>
                    ({dashboardData.growth.posts.current} this {period})
                  </span>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Pets"
                  value={dashboardData.summary.totalPets}
                  prefix={<AppstoreOutlined />}
                  valueStyle={{ color: '#fa8c16' }}
                />
                <div style={{ marginTop: 8 }}>
                  <span style={{ color: dashboardData.growth.pets.trend === 'up' ? '#52c41a' : '#ff4d4f' }}>
                    {dashboardData.growth.pets.trend === 'up' ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                    {formatGrowth(dashboardData.growth.pets.growth)}
                  </span>
                  <span style={{ marginLeft: 8, color: '#8c8c8c', fontSize: '12px' }}>
                    ({dashboardData.growth.pets.current} this {period})
                  </span>
                </div>
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Reports"
                  value={dashboardData.summary.totalReports}
                  prefix={<ExclamationCircleOutlined />}
                  valueStyle={{ color: '#ff4d4f' }}
                />
                <div style={{ marginTop: 8 }}>
                  <span style={{ color: dashboardData.growth.reports.trend === 'up' ? '#ff4d4f' : '#52c41a' }}>
                    {dashboardData.growth.reports.trend === 'up' ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                    {formatGrowth(dashboardData.growth.reports.growth)}
                  </span>
                  <span style={{ marginLeft: 8, color: '#8c8c8c', fontSize: '12px' }}>
                    ({dashboardData.growth.reports.current} this {period})
                  </span>
                </div>
              </Card>
            </Col>
          </Row>
        </div>
      ),
    },
    {
      key: 'users',
      label: '👥 Users',
      children: userAnalytics && (
        <div>
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Users"
                  value={userAnalytics.statusBreakdown.total}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Active Users"
                  value={userAnalytics.statusBreakdown.active}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Verified Users"
                  value={userAnalytics.statusBreakdown.verified}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Banned Users"
                  value={userAnalytics.statusBreakdown.banned}
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Card>
            </Col>
          </Row>
          <Card title="Daily User Registration">
            <Table
              dataSource={userAnalytics.dailyStats}
              columns={[
                { title: 'Date', dataIndex: '_id', key: 'date' },
                { title: 'New Users', dataIndex: 'count', key: 'count' },
                { title: 'Active Users', dataIndex: 'activeCount', key: 'activeCount' },
              ]}
              rowKey="_id"
              pagination={false}
              size="small"
            />
          </Card>
        </div>
      ),
    },
    {
      key: 'posts',
      label: '📝 Posts',
      children: postAnalytics && (
        <div>
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Posts"
                  value={postAnalytics.statusBreakdown.total}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Active Posts"
                  value={postAnalytics.statusBreakdown.active}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Suspended Posts"
                  value={postAnalytics.statusBreakdown.suspended}
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Rejected Posts"
                  value={postAnalytics.statusBreakdown.rejected}
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Card>
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card title="Most Liked Posts">
                <Table
                  dataSource={postAnalytics.mostLikedPosts}
                  columns={[
                    {
                      title: 'Post',
                      key: 'post',
                      render: (record: any) => (
                        <div>
                          <div style={{ fontWeight: 500 }}>
                            {record.caption?.substring(0, 50) || 'No caption'}...
                          </div>
                          <div style={{ fontSize: '12px', color: '#999' }}>
                            by @{record.user?.username || 'Unknown'}
                          </div>
                        </div>
                      ),
                    },
                    {
                      title: 'Likes',
                      dataIndex: 'likesCount',
                      key: 'likesCount',
                      render: (count: number) => <Tag color="red">❤️ {count}</Tag>,
                    },
                    {
                      title: 'Comments',
                      dataIndex: 'commentsCount',
                      key: 'commentsCount',
                      render: (count: number) => <Tag color="blue">💬 {count}</Tag>,
                    },
                  ]}
                  rowKey="_id"
                  pagination={false}
                  size="small"
                />
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title="Most Commented Posts">
                <Table
                  dataSource={postAnalytics.mostCommentedPosts}
                  columns={[
                    {
                      title: 'Post',
                      key: 'post',
                      render: (record: any) => (
                        <div>
                          <div style={{ fontWeight: 500 }}>
                            {record.caption?.substring(0, 50) || 'No caption'}...
                          </div>
                          <div style={{ fontSize: '12px', color: '#999' }}>
                            by @{record.user?.username || 'Unknown'}
                          </div>
                        </div>
                      ),
                    },
                    {
                      title: 'Comments',
                      dataIndex: 'commentsCount',
                      key: 'commentsCount',
                      render: (count: number) => <Tag color="blue">💬 {count}</Tag>,
                    },
                    {
                      title: 'Likes',
                      dataIndex: 'likesCount',
                      key: 'likesCount',
                      render: (count: number) => <Tag color="red">❤️ {count}</Tag>,
                    },
                  ]}
                  rowKey="_id"
                  pagination={false}
                  size="small"
                />
              </Card>
            </Col>
          </Row>
        </div>
      ),
    },
    {
      key: 'products',
      label: '🛍️ Products',
      children: mostViewedProducts && (
        <div>
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card title="Most Viewed Products" extra={<EyeOutlined />}>
                <Table
                  dataSource={mostViewedProducts.mostViewedPets}
                  columns={mostViewedColumns}
                  rowKey="_id"
                  pagination={false}
                  size="small"
                />
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title="Most Contacted Products" extra={<ShoppingOutlined />}>
                <Table
                  dataSource={mostViewedProducts.mostContactedPets}
                  columns={mostViewedColumns}
                  rowKey="_id"
                  pagination={false}
                  size="small"
                />
              </Card>
            </Col>
          </Row>
        </div>
      ),
    },
    {
      key: 'reports',
      label: '⚠️ Reports',
      children: (
        <Card title="Most Reported Users" extra={<TrophyOutlined />}>
          <Table
            dataSource={mostReportedUsers}
            columns={mostReportedColumns}
            rowKey={(record) => record.user._id}
            pagination={false}
          />
        </Card>
      ),
    },
    {
      key: 'categories',
      label: '📂 Categories',
      children: categoryAnalytics && (
        <div>
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card title="Pet Category Analytics">
                {categoryAnalytics.petCategories.map((cat, index) => (
                  <div key={index} style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontWeight: 500 }}>{cat.category.name}</span>
                      <span>{cat.petCount} pets</span>
                    </div>
                    <Progress
                      percent={Math.min((cat.petCount / (categoryAnalytics.petCategories[0]?.petCount || 1)) * 100, 100)}
                      showInfo={false}
                    />
                    <div style={{ fontSize: '12px', color: '#999', marginTop: 4 }}>
                      {cat.availableCount} available • {cat.totalViews} views • {cat.totalContacts} contacts
                      {cat.avgPrice > 0 && ` • Avg: ₹${cat.avgPrice.toFixed(0)}`}
                    </div>
                  </div>
                ))}
              </Card>
            </Col>
          </Row>
        </div>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>Analytics & Monitoring</Title>
        </Col>
        <Col>
          <Select
            value={period}
            onChange={setPeriod}
            style={{ width: 120 }}
          >
            <Option value="week">Last Week</Option>
            <Option value="month">Last Month</Option>
            <Option value="year">Last Year</Option>
          </Select>
        </Col>
      </Row>

      {loading && (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      )}

      {!loading && (
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
        />
      )}
    </div>
  );
};

export default Analytics;

